import { useState } from 'react';
import { sendEnvelope } from '../lib/api';
import { friendlyError } from '../lib/errors';
import {
  RECIPIENT_ROLE_INFO, CODE_CHANNEL_LABELS, routingWaves, isActionable,
  checkSendReadiness, type Recipient,
} from '@beckersign/shared';

interface Props {
  envelopeId: string;
  envelopeName: string;
  recipients: Recipient[];
  roles: { roleId: string; name: string }[];
  fields: { roleId: string; required: boolean }[];
  documentCount: number;
  status: string;
}

/**
 * Pre-send confirmation.
 *
 * Exists because the Gmail API gives us no bounce webhooks (Q-22): a mistyped
 * address fails silently and the envelope simply sits there. The only place a
 * typo can still be caught cheaply is before sending, so every address is shown
 * once more at a readable size and must be explicitly confirmed.
 */
export default function SendPanel({
  envelopeId, envelopeName, recipients, roles, fields, documentCount, status,
}: Props) {
  const [confirming, setConfirming] = useState(false);
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<{ notified: number; failures: string[]; ccCount: number } | null>(null);

  const readiness = checkSendReadiness(recipients, roles, fields, documentCount);
  const waves = routingWaves(recipients);
  const ccs = recipients.filter((r) => !isActionable(r));

  if (status !== 'draft') {
    return (
      <div className="notice">
        This envelope has been sent. Current status: <code>{status}</code>.
      </div>
    );
  }

  async function doSend() {
    setBusy(true); setErr(null);
    try {
      const res = await sendEnvelope({ envelopeId });
      setResult(res.data);
      setConfirming(false);
    } catch (e) {
      setErr(friendlyError(e, 'Could not send.'));
    } finally { setBusy(false); }
  }

  if (result) {
    return (
      <div className="notice" style={{ borderColor: 'var(--ok)' }} role="status">
        <strong>Sent.</strong> {result.notified} invitation{result.notified === 1 ? '' : 's'} went out
        {result.ccCount > 0 && `; ${result.ccCount} CC recipient${result.ccCount === 1 ? '' : 's'} will receive the completed documents at the end`}.
        {result.failures.length > 0 && (
          <p className="bad" style={{ margin: '.5rem 0 0' }}>
            Failed to send to: {result.failures.join(', ')}. Check the audit trail.
          </p>
        )}
      </div>
    );
  }

  if (!readiness.ready) {
    return (
      <>
        <h2>Send</h2>
        <p className="muted">Not ready yet:</p>
        <ul className="muted" style={{ fontSize: '.9rem' }}>
          {readiness.problems.map((p) => <li key={p}>{p}</li>)}
        </ul>
      </>
    );
  }

  return (
    <>
      <h2>Send</h2>

      {!confirming ? (
        <>
          <p className="muted">
            {waves.length === 1
              ? `All ${waves[0]!.length} recipient${waves[0]!.length === 1 ? '' : 's'} will be notified at once.`
              : `${waves.length} routing groups. Only the first is notified now; later groups are notified as earlier ones finish.`}
          </p>
          <button className="primary" style={{ width: 'auto' }} onClick={() => setConfirming(true)}>
            Review and send
          </button>
        </>
      ) : (
        <div style={{ border: '2px solid var(--accent)', borderRadius: 8, padding: '1rem' }}>
          <h3 style={{ marginTop: 0 }}>Check these addresses carefully</h3>
          <p className="muted" style={{ fontSize: '.85rem' }}>
            BeckerSign sends through Gmail, which gives no bounce notification. If an address is
            wrong the invitation vanishes silently and the envelope will simply sit unsigned.
            <strong> This is the last place a typo can be caught.</strong>
          </p>

          {waves.map((wave, i) => (
            <div key={i} style={{ marginTop: '.85rem' }}>
              <div className="muted" style={{ fontSize: '.78rem', textTransform: 'uppercase', letterSpacing: '.05em' }}>
                {waves.length > 1 ? `Group ${i + 1}${i === 0 ? ' — notified now' : ' — notified later'}` : 'Recipients'}
              </div>
              {wave.map((r) => (
                <div key={r.id} style={{ padding: '.45rem 0', borderBottom: '1px solid var(--line)' }}>
                  <div style={{ fontSize: '1.05rem', fontWeight: 600, letterSpacing: '.01em' }}>{r.email}</div>
                  <div className="muted" style={{ fontSize: '.8rem' }}>
                    {r.name} · {RECIPIENT_ROLE_INFO[r.recipientRole].label}
                    {r.authMethod === 'access_code' && r.codeChannel && (
                      <> · code by {CODE_CHANNEL_LABELS[r.codeChannel].toLowerCase()}</>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ))}

          {ccs.length > 0 && (
            <div style={{ marginTop: '.85rem' }}>
              <div className="muted" style={{ fontSize: '.78rem', textTransform: 'uppercase' }}>
                Copies at completion — no link sent now
              </div>
              {ccs.map((r) => (
                <div key={r.id} style={{ fontSize: '1rem', padding: '.3rem 0' }}>{r.email}</div>
              ))}
            </div>
          )}

          {recipients.some((r) => r.authMethod === 'access_code') && (
            <p className="muted" style={{ fontSize: '.82rem', marginTop: '.85rem' }}>
              Access codes are <strong>not</strong> included in the invitation. Deliver each one by
              phone, text or in person before the recipient tries to sign.
            </p>
          )}

          <label style={{ display: 'flex', gap: '.5rem', alignItems: 'flex-start', marginTop: '1rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)}
                   style={{ marginTop: '.25rem', width: 18, height: 18 }} />
            <span>I have checked every address above and they are correct.</span>
          </label>

          {err && <div className="alert" role="alert" style={{ marginTop: '.75rem' }}>{err}</div>}

          <div className="row" style={{ gap: '.5rem', marginTop: '1rem', justifyContent: 'flex-start' }}>
            <button className="primary" style={{ width: 'auto' }}
                    disabled={!checked || busy} onClick={() => void doSend()}>
              {busy ? 'Sending…' : `Send "${envelopeName}"`}
            </button>
            <button onClick={() => { setConfirming(false); setChecked(false); }} disabled={busy}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </>
  );
}
